import { useState, type FormEvent } from 'react';
import { api, BladApi, type Kontrahent } from '../api';
import { useIdOperacji } from '../operacje';
import { useUstawienia } from '../ustawienia';
import { idz } from '../router';
import { IkonaWstecz } from '../components/Icons';
import { NaglowekWidoku } from '../components/Shell';
import { WyborKategorii } from '../components/WyborKategorii';
import { WyborKontrahenta } from '../components/WyborKontrahenta';
import {
  WyborZdjec,
  ZDJECIA_KARTY,
  wyslijZdjecia,
  type ZdjecieWFormularzu,
} from '../components/Zdjecia';
import { Karta, Komunikat, PoleTekstowe, Przycisk, PrzyciskMarki } from '../components/ui';

/**
 * Zakładanie karty urządzenia — ścieżka „przyjęcie na serwis" z docs/model-danych.md §6,
 * na którą model przewiduje 20 sekund.
 *
 * Cztery rzeczy do wypełnienia stoją w wyróżnionym bloku, a poniżej tej granicy nie ma
 * na tym ekranie nic — reszta karty dosypuje się przy kolejnych wizytach
 * (CLAUDE.md §1 zasady 2, 4 i 8).
 */
export function NewDeviceView() {
  const operacja = useIdOperacji();
  const ustawienia = useUstawienia();
  const [blad, setBlad] = useState<string | null>(null);
  const [zdjecia, setZdjecia] = useState<ZdjecieWFormularzu[]>([]);
  const [wysylanie, setWysylanie] = useState(false);

  const [klient, setKlient] = useState<Kontrahent | null>(null);
  const [serialNumber, setSerialNumber] = useState('');
  const [producent, setProducent] = useState('');
  const [model, setModel] = useState('');
  const [categoryId, setCategoryId] = useState('');

  const polaGotowe =
    klient && serialNumber.trim() && producent.trim() && model.trim() && categoryId;

  // Zdjęcia blokują zapis tylko wtedy, gdy firma tak ustawiła (ekran Ustawienia).
  // Model nazywa je obowiązkowymi, zasada 2 mówi „nic poza trzema polami" — i to
  // nie kod ma tę sprzeczność rozstrzygać.
  const brakZdjec =
    ustawienia.zdjeciaWymaganePrzyKarcie &&
    ZDJECIA_KARTY.some((kind) => !zdjecia.some((z) => z.kind === kind));

  const gotowe = polaGotowe && !brakZdjec;

  async function wyslij(zdarzenie: FormEvent): Promise<void> {
    zdarzenie.preventDefault();
    if (!gotowe || wysylanie) return;

    setWysylanie(true);
    setBlad(null);
    try {
      const karta = await api.utworzUrzadzenie({
        clientOperationId: operacja.biezacy(),
        customerId: klient.id,
        categoryId,
        serialNumber: serialNumber.trim(),
        producent: producent.trim(),
        model: model.trim(),
      });
      operacja.zakoncz();
      // Zdjęcia idą po karcie i jej nie wywracają — patrz komentarz przy wyslijZdjecia.
      await wyslijZdjecia('DEVICE', karta.urzadzenie.id, zdjecia);
      idz(`/urzadzenia/${karta.urzadzenie.id}`);
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się założyć karty.');
    } finally {
      setWysylanie(false);
    }
  }

  return (
    <>
      <NaglowekWidoku
        tytul="Nowa karta"
        podtytul="Przyjęcie maszyny na serwis"
        akcje={
          <Przycisk
            type="button"
            onClick={() => idz('/urzadzenia')}
            ikona={<IkonaWstecz rozmiar={16} />}
          >
            Anuluj
          </Przycisk>
        }
      />

      <form onSubmit={wyslij} className="max-w-[720px]" noValidate>
        <Karta
          tytul="Wymagane — cztery pola"
          wyroznienie
          akcje={<span className="t-small text-n-600">numer boczny i kod QR nada system</span>}
        >
          <div className="mb-14">
            <WyborKontrahenta wybrany={klient} onWybor={setKlient} />
          </div>

          <div className="mb-14 grid gap-13 sm:grid-cols-2">
            <div>
              <PoleTekstowe
                etykieta="Numer fabryczny"
                value={serialNumber}
                onChange={(zdarzenie) => setSerialNumber(zdarzenie.target.value)}
                autoComplete="off"
              />
              <div className="mt-5">
                <Przycisk type="button" onClick={() => setSerialNumber('brak')}>
                  Tabliczka nieczytelna — wpisz „brak"
                </Przycisk>
              </div>
            </div>

            <WyborKategorii wartosc={categoryId} onZmiana={setCategoryId} />
          </div>

          <div className="grid gap-13 sm:grid-cols-2">
            <PoleTekstowe
              etykieta="Producent"
              value={producent}
              onChange={(zdarzenie) => setProducent(zdarzenie.target.value)}
              autoComplete="off"
            />
            <PoleTekstowe
              etykieta="Model"
              value={model}
              onChange={(zdarzenie) => setModel(zdarzenie.target.value)}
              autoComplete="off"
            />
          </div>
        </Karta>

        <Karta
          tytul="Zdjęcia"
          wyroznienie={ustawienia.zdjeciaWymaganePrzyKarcie}
          akcje={
            <span className="t-small text-n-600">
              {ustawienia.zdjeciaWymaganePrzyKarcie ? 'wymagane' : 'zalecane, nie blokują zapisu'}
            </span>
          }
        >
          <WyborZdjec
            zdjecia={zdjecia}
            onZmiana={setZdjecia}
            rodzaje={ZDJECIA_KARTY}
            wymagane={ustawienia.zdjeciaWymaganePrzyKarcie}
          />
          <p className="t-small mt-13 text-n-500">
            Zdjęcie tabliczki jest cenne samo w sobie — nawet gdy nikt nie przepisze z niego
            danych, informacja zostaje w systemie.
          </p>
        </Karta>

        {blad && (
          <div className="mb-14">
            <Komunikat tresc={blad} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-13">
          <PrzyciskMarki type="submit" disabled={!gotowe || wysylanie}>
            {wysylanie ? 'Zakładanie…' : 'Załóż kartę'}
          </PrzyciskMarki>
          <span className="t-small text-n-500">
            {brakZdjec
              ? 'Firma wymaga zdjęcia ogólnego i tabliczki.'
              : 'Resztę karty uzupełnisz przy kolejnych wizytach.'}
          </span>
        </div>
      </form>
    </>
  );
}
